/**
 * Serializes a complete project file into markdown format.
 *
 * Produces a document with front-matter followed by all sections
 * in canonical order: Overview, Team, Backlog, Sprints, Tasks, Reports, Change Log.
 *
 * @param project - The project file to serialize.
 * @returns Serialized markdown string.
 */
export function serializeProjectFile(project) {
    const lines = [];
    // Front-matter
    lines.push(serializeFrontMatter(project.frontMatter));
    lines.push('');
    // Sections in canonical order
    lines.push('## Overview');
    lines.push('');
    lines.push(project.sections.overview);
    lines.push('');
    lines.push('## Team');
    lines.push('');
    lines.push(serializeTeam(project.sections.team));
    lines.push('');
    lines.push('## Backlog');
    lines.push('');
    lines.push(serializeBacklogScrum(project.sections.backlog));
    lines.push('');
    lines.push('## Sprints');
    lines.push('');
    lines.push(serializeSprints(project.sections.sprints));
    lines.push('');
    lines.push('## Tasks');
    lines.push('');
    for (const task of project.sections.tasks) {
        lines.push(serializeTask(task));
        lines.push('');
    }
    lines.push('## Reports');
    lines.push('');
    lines.push(serializeReports(project.sections.reports));
    lines.push('');
    lines.push('## Change Log');
    lines.push('');
    lines.push(serializeChangeLog(project.sections.changeLog));
    lines.push('');
    return lines.join('\n');
}
/**
 * Serializes a single named section to markdown.
 *
 * @param name - Section heading name.
 * @param content - Section body content.
 * @returns Serialized markdown for the section.
 */
export function serializeSection(name, content) {
    const lines = [];
    lines.push(`## ${name}`);
    lines.push('');
    lines.push(content);
    lines.push('');
    return lines.join('\n');
}
/**
 * Serializes a list of tasks into a kanban-style backlog table.
 *
 * @param tasks - Array of tasks to serialize.
 * @returns Markdown table string.
 */
export function serializeBacklog(tasks) {
    const lines = [];
    lines.push('| ID | Title | Priority | Story Points | Status | Assignee |');
    lines.push('|----|-------|----------|-------------|--------|----------|');
    for (const task of tasks) {
        lines.push(`| ${task.id} | ${task.title} | ${task.priority} | ${task.storyPoints ?? ''} | ${task.status} | ${task.assignee ?? ''} |`);
    }
    return lines.join('\n');
}
/**
 * Serializes epics and their user stories into scrum backlog format.
 *
 * @param epics - Array of epics to serialize.
 * @returns Serialized markdown with epic headings and checklist items.
 */
export function serializeBacklogScrum(epics) {
    const lines = [];
    for (const epic of epics) {
        lines.push(`### ${epic.id}: ${epic.title}`);
        lines.push('');
        for (const us of epic.userStories) {
            lines.push(`- [${us.completed ? 'x' : ' '}] ${us.id}: ${us.description}`);
        }
        lines.push('');
    }
    return lines.join('\n');
}
/**
 * Serializes sprints into markdown format with metadata tables.
 *
 * @param sprints - Array of sprints to serialize.
 * @returns Serialized markdown for all sprints.
 */
export function serializeSprints(sprints) {
    const lines = [];
    for (const sprint of sprints) {
        lines.push(`### ${sprint.name}`);
        lines.push('');
        lines.push('| Field | Value |');
        lines.push('|-------|-------|');
        lines.push(`| Start Date | ${sprint.startDate} |`);
        lines.push(`| End Date | ${sprint.endDate} |`);
        lines.push(`| Status | ${sprint.status} |`);
        lines.push(`| Committed Points | ${sprint.committedPoints} |`);
        lines.push(`| Completed Points | ${sprint.completedPoints} |`);
        lines.push(`| Velocity | ${sprint.velocity} |`);
        lines.push(`| Sprint Goal: ${sprint.goal} |`);
        lines.push('');
    }
    return lines.join('\n');
}
/**
 * Serializes reports into markdown format.
 *
 * @param reports - Array of reports to serialize.
 * @returns Serialized markdown for all reports.
 */
export function serializeReports(reports) {
    const lines = [];
    for (const report of reports) {
        lines.push(`### ${report.id}: ${report.type}`);
        lines.push('');
        lines.push(`Date: ${report.date}`);
        lines.push('');
        lines.push('```json');
        lines.push(JSON.stringify(report.data, null, 2));
        lines.push('```');
        lines.push('');
    }
    return lines.join('\n');
}
/**
 * Serializes flow configuration into markdown.
 *
 * @param flow - Array of flow config entries.
 * @returns Serialized markdown table.
 */
export function serializeFlowConfig(flow) {
    const lines = [];
    lines.push('| Column | WIP Limit | Description |');
    lines.push('|--------|-----------|-------------|');
    for (const entry of flow) {
        lines.push(`| ${entry.column} | ${entry.wipLimit ?? '∞'} | ${entry.description} |`);
    }
    return lines.join('\n');
}
// --- Internal helpers ---
function serializeFrontMatter(data) {
    const lines = ['---'];
    lines.push(`project_id: ${data.project_id}`);
    lines.push(`name: "${data.name}"`);
    lines.push(`workflow: ${data.workflow}`);
    lines.push(`default_branch: ${data.default_branch}`);
    lines.push(`created: ${data.created}`);
    lines.push(`updated: ${data.updated}`);
    lines.push(`status: ${data.status}`);
    if (data.tags && data.tags.length > 0) {
        lines.push(`tags: [${data.tags.join(', ')}]`);
    }
    lines.push('config:');
    if (data.config.sprint_duration) {
        lines.push(`  sprint_duration: ${data.config.sprint_duration}`);
    }
    if (data.config.wip_limit) {
        lines.push(`  wip_limit: ${data.config.wip_limit}`);
    }
    if (data.config.story_point_scale) {
        lines.push(`  story_point_scale: [${data.config.story_point_scale.join(', ')}]`);
    }
    if (data.config.timezone) {
        lines.push(`  timezone: "${data.config.timezone}"`);
    }
    if (data.config.states && data.config.states.length > 0) {
        lines.push('  states:');
        for (const s of data.config.states) {
            lines.push(`    - id: ${s.id}`);
            lines.push(`      name: ${s.name}`);
            lines.push(`      color: ${s.color}`);
            lines.push(`      order: ${s.order}`);
        }
    }
    lines.push('---');
    return lines.join('\n');
}
function serializeTeam(members) {
    const lines = [];
    lines.push('| Name | Role | Username | Availability |');
    lines.push('|------|------|----------|--------------|');
    for (const m of members) {
        lines.push(`| ${m.name} | ${m.role} | ${m.username} | ${m.availability ?? '—'} |`);
    }
    return lines.join('\n');
}
function serializeTask(task) {
    const lines = [];
    lines.push(`### ${task.id}: ${task.title}`);
    lines.push('');
    lines.push('| Field | Value |');
    lines.push('|-------|-------|');
    lines.push(`| Status | ${task.status} |`);
    lines.push(`| Priority | ${task.priority} |`);
    if (task.assignee)
        lines.push(`| Assignee | ${task.assignee} |`);
    if (task.storyPoints)
        lines.push(`| Story Points | ${task.storyPoints} |`);
    if (task.sprint)
        lines.push(`| Sprint | ${task.sprint} |`);
    if (task.labels.length > 0)
        lines.push(`| Labels | ${task.labels.join(', ')} |`);
    lines.push(`| Created | ${task.created} |`);
    lines.push(`| Updated | ${task.updated} |`);
    if (task.started)
        lines.push(`| Started | ${task.started} |`);
    lines.push('');
    if (task.description) {
        lines.push('#### Description');
        lines.push('');
        lines.push(task.description);
        lines.push('');
    }
    if (task.acceptanceCriteria.length > 0) {
        lines.push('#### Acceptance Criteria');
        lines.push('');
        for (const ac of task.acceptanceCriteria) {
            lines.push(`- [${ac.completed ? 'x' : ' '}] ${ac.text}`);
        }
        lines.push('');
    }
    lines.push('---');
    return lines.join('\n');
}
function serializeChangeLog(entries) {
    const lines = [];
    lines.push('| # | Timestamp | User | Action | Target | Details |');
    lines.push('|---|-----------|------|--------|--------|---------|');
    for (const entry of entries) {
        lines.push(`| ${entry.number} | ${entry.timestamp} | ${entry.user} | ${entry.action} | ${entry.target} | ${entry.details} |`);
    }
    return lines.join('\n');
}
//# sourceMappingURL=save.js.map